import * as tz from 'date-fns-tz/esm/fp';
import { function as F } from 'fp-ts';
import { Eq, eqString } from 'fp-ts/Eq';
import { fromCompare } from 'fp-ts/Ord';
import { Brand } from './brand';
import { fns } from './date-fns';
import { zeroPadStart } from '@macs-frontend/core/utils/time';
import { AppDate } from '@macs-frontend/core/utils/date';
import { zonedTimeToUtc } from 'date-fns-tz';

// RFC3339: 2020-10-22T18:37:34.355+01:00
export type AppDateTime = Brand<string, 'AppDateTime'>;

export function isValidAppDateTime(value?: string | null): value is AppDateTime {
  if (!value) {
    return false;
  }

  return F.pipe(
    value,
    fns.parse(new Date())("yyyy-MM-dd'T'HH:mm:ss.SSSxxx"),
    fns.isValid,
  );
}

export function parseAppDateTime(value: AppDateTime): Date {
  return F.pipe(new Date(value), tz.utcToZonedTime('UTC'));
}

export function toAppDateTime(value: string | Date, utcDate = false): AppDateTime {
  if (value instanceof Date) {
    if (utcDate) {
      return F.pipe(value, tz.zonedTimeToUtc('UTC'), fns.formatRFC3339) as AppDateTime;
    }

    return fns.formatRFC3339(value) as AppDateTime;
  }

  if (!isValidAppDateTime(value)) {
    throw new Error('Could not parse app date.');
  }

  return F.pipe(value, fns.parseISO, fns.formatRFC3339) as AppDateTime;
}

export const eqAppDateTime = eqString as Eq<AppDateTime>;

export const ordAppDateTime = fromCompare((a?: AppDateTime, b?: AppDateTime) => {
  if (a === b) {
    return 0;
  }

  if (!a) {
    return -1;
  }

  if (!b) {
    return 1;
  }

  const aDate = parseAppDateTime(a);
  const bDate = parseAppDateTime(b);

  return aDate > bDate ? 1 : -1;
});
